import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
  Alert,
  Stack,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Assessment as AssessmentIcon,
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
  Launch as LaunchIcon,
} from '@mui/icons-material';
import { ReportType } from '../types';

interface GenerateModalProps {
  open: boolean;
  onClose: () => void;
  reportType: ReportType;
  isGenerating: boolean;
  progress: number;
  reportUrl?: string;
  downloadUrl?: string;
  priorInitialFound?: boolean;
  error?: string | null;
  onRetry?: () => void;
}

export const GenerateModal: React.FC<GenerateModalProps> = ({
  open,
  onClose,
  reportType,
  isGenerating,
  progress,
  reportUrl,
  downloadUrl,
  priorInitialFound,
  error,
  onRetry,
}) => {
  const handleClose = () => {
    if (!isGenerating) onClose();
  };

  // G4: a Review with no prior Initial is generated as a standalone report.
  const showNoPriorNote =
    !error && reportType === 'Review' && priorInitialFound === false;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <AssessmentIcon color="primary" />
            <Typography variant="h6">Generate {reportType} Lactate Report</Typography>
          </Box>
          {!isGenerating && (
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={3}>
          {(isGenerating || progress > 0) && !error && (
            <Box>
              <LinearProgress
                variant={progress >= 100 ? 'determinate' : 'indeterminate'}
                value={progress >= 100 ? 100 : undefined}
                sx={{ height: 8, borderRadius: 1, mb: 1 }}
              />
              <Typography variant="body2" color="text.secondary" align="center">
                {progress >= 100 ? 'Report generated successfully!' : 'Generating report…'}
              </Typography>
            </Box>
          )}
          {progress >= 100 && reportUrl && !error && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main' }} />
              </Box>
              <Alert severity="success" sx={{ borderRadius: 1 }}>
                Your {reportType.toLowerCase()} lactate report is ready.
              </Alert>
            </Box>
          )}
          {showNoPriorNote && (
            <Alert severity="info" sx={{ borderRadius: 1 }}>
              No prior Initial found for this modality - generated as a standalone report without comparison.
            </Alert>
          )}
          {error && (
            <Box>
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
              {onRetry && (
                <Box display="flex" justifyContent="center">
                  <Button variant="outlined" onClick={onRetry} startIcon={<AssessmentIcon />}>
                    Retry Generation
                  </Button>
                </Box>
              )}
            </Box>
          )}
          {isGenerating && !error && (
            <Alert severity="info" sx={{ borderRadius: 1 }}>
              <Typography variant="body2">
                Report generation can take a minute or two. Please don't close this window.
              </Typography>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 0 }}>
        {reportUrl && !isGenerating ? (
          <>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              href={downloadUrl || reportUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Download Report
            </Button>
            <Button
              variant="contained"
              startIcon={<LaunchIcon />}
              href={reportUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open Report
            </Button>
          </>
        ) : (
          <Button onClick={handleClose} disabled={isGenerating} variant={error ? 'contained' : 'text'}>
            {error ? 'Close' : isGenerating ? 'Please wait…' : 'Cancel'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
